<template>
    <div id="edit-whatsapp-cloud-page">
        <ChannelForm
            ref="channelForm"
            :steps="formSteps"
            :current-step="currentStep"
            @update:currentStep="setStep"
            @step-change="onStepChange"
        >
            <template #default="{ previousStep, close }">
                <!-- Configure Step -->
                <StepConfigure
                    v-if="currentStep === 'configure'"
                    ref="stepConfigure"
                    :sender="sender"
                    :place-sender="localPlaceSender"
                    channel="whatsapp-cloud"
                    @saved="onConfigureSaved"
                    @cancel="close"
                />

                <!-- Test Step -->
                <StepTestWhatsappCloud
                    v-if="currentStep === 'test'"
                    :templates="templates"
                    :initial-options="localPlaceSender && localPlaceSender.whatsapp_cloud_options ? localPlaceSender.whatsapp_cloud_options : null"
                    :testing="testing"
                    @back="previousStep"
                    @test="testConnection"
                />

                <!-- Finished Step -->
                <StepFinished
                    v-if="currentStep === 'finished'"
                    channel="whatsapp-cloud"
                    @back="previousStep"
                    @retry-test="setStep('test')"
                    @done="$emit('done')"
                />
            </template>
        </ChannelForm>
    </div>
</template>
<script>
import keviApi from '../../../keviApi';
import ChannelForm from './channel-form.vue';
import StepConfigure from './steps/step-configure.vue';
import StepFinished from './steps/step-finished.vue';
import StepTestWhatsappCloud from './steps/step-test-whatsapp-cloud.vue';

export default {
    name: 'edit-whatsapp-cloud-page',
    components: {
        ChannelForm,
        StepConfigure,
        StepTestWhatsappCloud,
        StepFinished,
    },
    props: {
        sender: {
            type: Object,
            required: true,
        },
        senders: {
            type: Array,
            default: () => [],
        },
        rawEditing: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            currentStep: 'configure',
            testing: false,
            placeSenderId: null,
            templates: [],
            loadingTemplates: false,
            localPlaceSender: {},
        };
    },
    computed: {
        formSteps() {
            return [
                { id: 'configure', label: this.t('settings-connections-steps-configure'), icon: 'ui-settings-02' },
                { id: 'test', label: this.t('settings-connections-steps-test'), icon: 'ui-loading-01' },
                { id: 'finished', label: this.t('settings-connections-steps-finish'), icon: 'ui-check-circle' },
            ];
        },
    },
    methods: {
        setStep(step) {
            if (['configure', 'test', 'finished'].includes(step)) {
                this.currentStep = step;
            }
        },
        onStepChange(step) {
            if (step === 'test') {
                this.loadTemplatesIfNeeded();
            }
        },
        onConfigureSaved(placeSender) {
            const merged = {
                ...(this.rawEditing || {}),
                ...(this.localPlaceSender || {}),
                ...(placeSender || {}),
            };
            if (!merged.fields && merged.form_fields) merged.fields = merged.form_fields;
            if (!merged.fields && placeSender?.fields) merged.fields = placeSender.fields;
            this.localPlaceSender = merged;

            this.placeSenderId = merged?.id || this.rawEditing?.id || null;
            this.$emit('saved', merged);
            this.$root.$emit('reload-senders');
            this.currentStep = 'test';
            this.loadTemplatesIfNeeded();
        },
        async loadTemplatesIfNeeded() {
            const id = this.placeSenderId || this.localPlaceSender?.id || this.rawEditing?.id;
            if (!id || this.loadingTemplates) return;

            this.loadingTemplates = true;
            try {
                const optionsTemplates = keviApi.getOptions(`senders/${id}/templates`, 'GET');
                const response = await this.$http(optionsTemplates);
                this.templates = response.body || [];
            } catch (error) {
                console.error('Failed to load templates', error);
                this.templates = [];
            } finally {
                this.loadingTemplates = false;
            }
        },
        async testConnection(payload) {
            this.testing = true;
            const placeSenderId = this.placeSenderId || this.localPlaceSender?.id || this.rawEditing?.id;
            if (!placeSenderId) {
                this.$store.dispatch('notification/error', this.$_('settings-connections-notifications-sender_id_not_found'));
                this.testing = false;
                return;
            }

            const testData = {
                places_senders_id: placeSenderId,
                phone_number: payload?.phone,
            };

            if (payload?.templateName) {
                testData.template_name = payload.templateName;
            }
            if (payload?.templateParams) {
                const params = payload.templateParams;
                testData.template_params = {
                    header_text: params.header_text || [],
                    body_text: params.body_text || [],
                    buttons: params.buttons || [],
                };
                if (params.header_handle && params.header_handle.length > 0) {
                    testData.template_params.header_handle = params.header_handle;
                }
                if (params.document_filename && String(params.document_filename).trim()) {
                    testData.template_params.document_filename = params.document_filename;
                }
                const locKeys = ['location_latitude', 'location_longitude', 'location_name', 'location_address'];
                for (const k of locKeys) {
                    if (params[k] !== undefined && params[k] !== null && String(params[k]).trim() !== '') {
                        testData.template_params[k] = params[k];
                    }
                }
                if (params.body_handle && params.body_handle.length > 0) {
                    testData.template_params.body_handle = params.body_handle;
                }
            }
            var options = keviApi.getOptions("senders/whatsapp-cloud/test", "POST", testData);
            this.$http(options).then((result) => {
                this.$store.dispatch('notification/success', this.$_('settings-connections-notifications-test_sent_success'));
                this.currentStep = 'finished';
            }, (error) => {
                const channel = 'whatsapp-cloud';
                let errorMessage = this.$_(`notifications-errors-${channel}-${error.body.error}`, null, this.$_('settings-connections-notifications-test_send_error', [error.body.message || ""]));
                this.$store.dispatch('notification/error', errorMessage);
            }).finally(() => {
                this.testing = false;
            });
        },
        close() {
            this.$root.$emit('close-drawer');
            this.$root.$emit('reload-senders');
        },
    },
    mounted() {
        if (this.rawEditing?.id) {
            this.placeSenderId = this.rawEditing.id;
        }
        this.localPlaceSender = this.rawEditing || {};
    },
    watch: {
        rawEditing: {
            handler() {
                if (this.rawEditing?.id) {
                    this.placeSenderId = this.rawEditing.id;
                }
                this.localPlaceSender = this.rawEditing || {};
            },
            deep: true,
            immediate: true,
        },
    },
}
</script>
<style scoped lang="less">
#edit-whatsapp-cloud-page {
    .box {
        padding: 20px;
    }
    .action-buttons {
        display: flex;
        gap: 12px;
        justify-content: stretch;
        button {
            flex: 1;
            justify-content: center;
        }
    }
    .senders-logos {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
        grid-gap: 20px;
        button {
            border: 4px solid transparent;
            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
                &.selected {
                    border: 4px solid var(--primary);
                }
            }
            &:not(:disabled) {
                cursor: pointer;
                &.selected,
                &:hover {
                    border: 4px solid var(--primary);
                }
            }
        }
    }
}
</style>